import { useState } from 'react';
import './LiabilitiesSection.css';

export default function LiabilitiesSection({ categories, profileData, onSave, saving }) {
  const existingSubCats = profileData?.selected_liability_sub_categories || {};
  const existingFieldResponses = profileData?.liability_field_responses || {};

  const initialSelectedCats = new Set(
    categories
      .filter(cat => (existingSubCats[cat.uid] || []).length > 0)
      .map(cat => cat.uid)
  );

  const [stage, setStage] = useState('select-cat'); // 'select-cat' | 'select-sub' | 'fill'
  const [selectedCats, setSelectedCats] = useState(initialSelectedCats);
  const [selectedSubCats, setSelectedSubCats] = useState({ ...existingSubCats });
  const [fieldResponses, setFieldResponses] = useState({ ...existingFieldResponses });
  const [collapsedCards, setCollapsedCards] = useState(new Set());

  function toggleCat(catUid) {
    if (selectedCats.has(catUid)) {
      const next = new Set(selectedCats);
      next.delete(catUid);
      setSelectedCats(next);
      const updated = { ...selectedSubCats };
      delete updated[catUid];
      setSelectedSubCats(updated);
    } else {
      const next = new Set(selectedCats);
      next.add(catUid);
      setSelectedCats(next);
    }
  }

  function isSubCatSelected(catUid, subCatUid) {
    const list = selectedSubCats[catUid] || [];
    return list.includes(subCatUid);
  }

  function toggleSubCat(catUid, subCatUid) {
    setSelectedSubCats(prev => {
      const current = [...(prev[catUid] || [])];
      const idx = current.indexOf(subCatUid);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(subCatUid);
      }
      return { ...prev, [catUid]: current };
    });
  }

  function isSubCatComplete(category, subCat) {
    const fields = category.fields || [];
    for (let i = 0; i < fields.length; i++) {
      if (!fields[i].required) continue;
      const val = getFieldValue(category.uid, subCat.uid, fields[i].uid);
      if (!val || val.toString().trim() === '') return false;
    }
    return fields.some(f => f.required);
  }

  function getSubCatFieldFillCount(category, subCat) {
    const fields = category.fields || [];
    let filled = 0;
    for (let i = 0; i < fields.length; i++) {
      const val = getFieldValue(category.uid, subCat.uid, fields[i].uid);
      if (val && val.toString().trim() !== '') filled++;
    }
    return filled;
  }

  function toggleCardCollapse(key) {
    setCollapsedCards(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function handleFieldChange(catUid, subCatUid, fieldUid, value) {
    const key = `${catUid}__${subCatUid}__${fieldUid}`;
    setFieldResponses(prev => ({ ...prev, [key]: value }));
  }

  function getFieldValue(catUid, subCatUid, fieldUid) {
    const key = `${catUid}__${subCatUid}__${fieldUid}`;
    return fieldResponses[key] || '';
  }

  function handleSave() {
    onSave(selectedSubCats, fieldResponses);
  }

  // Stats
  let totalSubSelected = 0;
  let totalComplete = 0;
  const allSelectedSubCats = [];

  for (let i = 0; i < categories.length; i++) {
    const cat = categories[i];
    const selected = selectedSubCats[cat.uid] || [];
    totalSubSelected += selected.length;
    for (let j = 0; j < selected.length; j++) {
      const sub = cat.sub_categories.find(s => s.uid === selected[j]);
      if (sub && isSubCatComplete(cat, sub)) totalComplete++;
      if (sub) allSelectedSubCats.push({ category: cat, subCategory: sub });
    }
  }

  const activeCats = categories.filter(cat => selectedCats.has(cat.uid));

  /* ── Stage 1: Category Cards ────────────────────────────────── */
  if (stage === 'select-cat') {
    return (
      <div className="liab-section">
        <div className="liab-section__stage-header">
          <div className="liab-section__stage-title">Which liabilities do you carry?</div>
          <div className="liab-section__stage-sub">Select all that apply — you can always update later</div>
        </div>

        <div className="liab-section__cat-grid-scroll">
          <div className="liab-section__cat-grid">
            {categories.map(cat => {
              const isSelected = selectedCats.has(cat.uid);
              const subCount = (cat.sub_categories || []).length;
              return (
                <button
                  key={cat.uid}
                  className={`liab-section__cat-card ${isSelected ? 'liab-section__cat-card--selected' : ''}`}
                  onClick={() => toggleCat(cat.uid)}
                >
                  {isSelected && (
                    <span className="liab-section__cat-card__check">✓</span>
                  )}
                  <span className="liab-section__cat-card__icon-wrap">
                    {cat.icon || '📋'}
                  </span>
                  <span className="liab-section__cat-card__name">{cat.category_name}</span>
                  {subCount > 0 && (
                    <span className="liab-section__cat-card__count">{subCount} type{subCount !== 1 ? 's' : ''}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="liab-section__footer">
          <span className="liab-section__count">
            {selectedCats.size === 0
              ? 'No liability categories selected yet'
              : `${selectedCats.size} categor${selectedCats.size !== 1 ? 'ies' : 'y'} selected`}
          </span>
          <button
            className="btn--primary"
            onClick={() => setStage('select-sub')}
            disabled={selectedCats.size === 0}
          >
            Choose types within each →
          </button>
        </div>
      </div>
    );
  }

  /* ── Stage 2: Sub-category Selection ────────────────────────── */
  if (stage === 'select-sub') {
    return (
      <div className="liab-section">
        <div className="liab-section__fill-header">
          <div className="liab-section__fill-header-left">
            <button
              className="liab-section__back-btn"
              onClick={() => setStage('select-cat')}
            >
              ← Liability categories
            </button>
            <div>
              <div className="liab-section__stage-title">Select specific types</div>
              <div className="liab-section__stage-sub">Pick the types you carry within each category</div>
            </div>
          </div>
        </div>

        <div className="liab-section__palette-scroll">
          {activeCats.map(cat => (
            <div key={cat.uid} className="liab-section__cat-group">
              <div className="liab-section__cat-header-palette">
                <span className="liab-section__cat-icon">{cat.icon || '📋'}</span>
                {cat.category_name}
              </div>
              <div className="liab-section__cat-row">
                {(cat.sub_categories || []).map(sub => {
                  const isSelected = isSubCatSelected(cat.uid, sub.uid);
                  return (
                    <button
                      key={sub.uid}
                      className={`liab-section__tile ${isSelected ? 'liab-section__tile--selected' : ''}`}
                      onClick={() => toggleSubCat(cat.uid, sub.uid)}
                    >
                      <span className="liab-section__tile-name">{sub.name}</span>
                      {isSelected && (
                        <>
                          <span className="liab-section__tile-check liab-section__tile-check--normal">✓</span>
                          <span className="liab-section__tile-check liab-section__tile-check--hover">×</span>
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="liab-section__footer">
          <span className="liab-section__count">
            {totalSubSelected === 0
              ? 'No types selected yet'
              : `${totalSubSelected} type${totalSubSelected !== 1 ? 's' : ''} selected`}
          </span>
          <button
            className="btn--primary"
            onClick={() => setStage('fill')}
            disabled={totalSubSelected === 0}
          >
            Continue to fill details →
          </button>
        </div>
      </div>
    );
  }

  /* ── Stage 3: Liability Details ─────────────────────────────── */
  return (
    <div className="liab-section">
      <div className="liab-section__fill-header">
        <div className="liab-section__fill-header-left">
          <button
            className="liab-section__back-btn"
            onClick={() => setStage('select-sub')}
          >
            ← Change types
          </button>
          <div>
            <div className="liab-section__stage-title">Fill in liability details</div>
            <div className="liab-section__stage-sub">{totalSubSelected} liabilit{totalSubSelected !== 1 ? 'ies' : 'y'} selected · {totalComplete} complete</div>
          </div>
        </div>
        <div className="liab-section__fill-progress-track">
          <div
            className={`liab-section__fill-progress-fill ${totalComplete === totalSubSelected && totalSubSelected > 0 ? 'liab-section__fill-progress-fill--done' : ''}`}
            style={{ width: `${totalSubSelected > 0 ? (totalComplete / totalSubSelected) * 100 : 0}%` }}
          />
        </div>
      </div>

      <div className="liab-section__grid-scroll">
        <div className="liab-section__grid">
          {allSelectedSubCats.map(({ category, subCategory }) => {
            const key = `${category.uid}__${subCategory.uid}`;
            const collapsed = collapsedCards.has(key);
            const complete = isSubCatComplete(category, subCategory);
            const fields = category.fields || [];
            const filled = getSubCatFieldFillCount(category, subCategory);

            return (
              <div
                key={key}
                className={`liab-section__card ${complete ? 'liab-section__card--complete' : ''} ${collapsed ? 'liab-section__card--collapsed' : ''}`}
              >
                <div className="liab-section__card-header">
                  <span className="liab-section__card-icon">{category.icon || '📋'}</span>
                  <div className="liab-section__card-meta">
                    <span className="liab-section__card-name">{subCategory.name}</span>
                    <span className="liab-section__card-cat">{category.category_name}</span>
                  </div>
                  {complete && !collapsed && (
                    <span className="liab-section__complete-badge">✓</span>
                  )}
                  <button
                    className="liab-section__collapse-btn"
                    onClick={() => toggleCardCollapse(key)}
                    title={collapsed ? 'Expand' : 'Minimise'}
                  >
                    {collapsed ? '▸' : '▾'}
                  </button>
                </div>

                {fields.length > 0 && (
                  <div className="liab-section__card-progress">
                    <div className="liab-section__card-progress-track">
                      <div
                        className="liab-section__card-progress-fill"
                        style={{
                          width: `${(filled / fields.length) * 100}%`,
                          backgroundColor: complete ? '#16a34a' : '#dc2626'
                        }}
                      />
                    </div>
                    <span className="liab-section__card-progress-label">{filled}/{fields.length}</span>
                  </div>
                )}

                {!collapsed && (
                  <div className="liab-section__card-body">
                    {fields.length > 0 ? (
                      <div className="liab-section__card-fields">
                        {fields.map(field => (
                          <div key={field.uid} className="liab-section__card-field">
                            <label className="liab-section__field-label">
                              {field.label}
                              {field.required && <span className="liab-section__required">*</span>}
                            </label>
                            {field.field_type === 'dropdown' ? (
                              <select
                                className="liab-section__select"
                                value={getFieldValue(category.uid, subCategory.uid, field.uid)}
                                onChange={e => handleFieldChange(category.uid, subCategory.uid, field.uid, e.target.value)}
                              >
                                <option value="">Select...</option>
                                {(field.options || []).map(opt => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            ) : (
                              <input
                                className="liab-section__input"
                                type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'}
                                value={getFieldValue(category.uid, subCategory.uid, field.uid)}
                                onChange={e => handleFieldChange(category.uid, subCategory.uid, field.uid, e.target.value)}
                                placeholder={field.label}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="liab-section__no-fields">No details needed for this liability type.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="liab-section__footer">
        <span className="liab-section__count">
          {totalComplete === totalSubSelected && totalSubSelected > 0 ? 'All liabilities detailed!' : ''}
        </span>
        <button className="btn--primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Liabilities'}
        </button>
      </div>
    </div>
  );
}
